// Shared template for the four domains of care. The three in-development
// domains render <DomainPage config={...} /> — configuration, not construction.
// The Housing page (live, deepest) is bespoke but reuses PricingTable and the
// same section grammar. See HousingDomain.jsx.
import { Section, Overline, PrimaryCTA, SecondaryCTA, TrustStrip } from "@/components/Section";
import WaitlistForm from "@/components/WaitlistForm";
import StatusChip from "@/components/StatusChip";
import { CheckCircle2 } from "lucide-react";

// Pricing table shared by all four domain pages.
// rows: { product, status ("LIVE"|"PLANNED"|null), who, model, price }
export function PricingTable({ rows, footnote, testid = "pricing-table" }) {
  return (
    <div data-testid={testid}>
      <div className="overflow-x-auto border border-slate-200">
        <table className="w-full text-sm md:text-base">
          <thead>
            <tr className="text-left bg-[#F8F9FA] border-b-2 border-[#FF8A00]">
              <th className="px-5 py-4 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Product</th>
              <th className="px-5 py-4 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Who it's for</th>
              <th className="px-5 py-4 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Model</th>
              <th className="px-5 py-4 text-xs font-black uppercase tracking-[0.18em] text-slate-500 whitespace-nowrap">Price (CAD)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.product} data-testid={`${testid}-row-${i}`} className="border-b border-slate-200 last:border-b-0 align-top">
                <td className="px-5 py-4 font-bold text-[#0A1224]">
                  {r.product}
                  {r.status && <StatusChip status={r.status} className="ml-2 align-middle" />}
                </td>
                <td className="px-5 py-4 text-slate-600">{r.who}</td>
                <td className="px-5 py-4 text-slate-600">{r.model}</td>
                <td className="px-5 py-4 text-[#0A1224] font-medium tabular-nums whitespace-nowrap">{r.price}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footnote && <p className="text-xs text-slate-500 mt-3">{footnote}</p>}
    </div>
  );
}

// config shape:
// {
//   slug, name, h1, lede,
//   problem: [para1, para2],
//   evidenceBase: { heading, headingAccent, body },
//   produces: [string, ...],
//   pricingNote, pricingRows, pricingFootnote,
//   faq: [{ q, a }, ...],
// }
export default function DomainPage({ config }) {
  const c = config;
  const t = c.slug; // testid prefix

  return (
    <>
      {/* PAGE HEADER */}
      <section data-testid={`${t}-page-header`} className="pt-24 pb-16 md:pt-32 md:pb-20 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <Overline>Solutions · {c.name}</Overline>
            <StatusChip status="IN DEVELOPMENT" />
          </div>
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] max-w-5xl">
            {c.h1}
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mt-8 max-w-3xl leading-relaxed">{c.lede}</p>
          <div className="flex flex-wrap gap-3 mt-10">
            <a
              href="#waitlist"
              data-testid={`${t}-waitlist-cta`}
              className="bg-[#FF8A00] text-[#050A15] font-bold px-6 py-3.5 hover:bg-[#FFA833] transition-colors inline-flex items-center gap-2 tracking-tight"
            >
              Join the waitlist for this domain
            </a>
            <SecondaryCTA to="/solutions/housing" testid={`${t}-live-today-cta`}>See what's live today</SecondaryCTA>
          </div>
        </div>
      </section>

      {/* WAITLIST — domain-tagged */}
      <Section testid={`${t}-waitlist`} className="!py-16 bg-[#F8F9FA]">
        <div id="waitlist" className="scroll-mt-24 max-w-2xl">
          <Overline className="mb-4">Join the waitlist</Overline>
          <WaitlistForm compact domain={c.name} testid={`${t}-waitlist-form`} />
        </div>
      </Section>

      {/* THE PROBLEM IN THIS SYSTEM */}
      <Section testid={`${t}-problem`}>
        <Overline className="mb-6">The problem in this system</Overline>
        <div className="space-y-5 text-slate-700 text-lg md:text-xl leading-relaxed max-w-4xl">
          {c.problem.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </Section>

      {/* BUILT ON THE SAME EVIDENCE BASE + PRODUCES CHECKLIST */}
      <Section testid={`${t}-evidence-base`} className="bg-[#F8F9FA]">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <Overline className="mb-6">Built on the same evidence base</Overline>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight">
              {c.evidenceBase.heading}{" "}
              {c.evidenceBase.headingAccent && (
                <span className="text-[#FF8A00]">{c.evidenceBase.headingAccent}</span>
              )}
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mt-6">{c.evidenceBase.body}</p>
          </div>
          <aside className="lg:col-span-5">
            <div className="bg-white border border-slate-200 p-8">
              <Overline className="mb-4">What this domain will produce</Overline>
              <ul className="space-y-4">
                {c.produces.map((x, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#0A1224]">
                    <CheckCircle2 className="text-[#FF8A00] flex-shrink-0 mt-1" size={18} />
                    <span className="font-medium">{x}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </Section>

      {/* PLANNED MODULES & PRICING */}
      <Section testid={`${t}-pricing`}>
        <div id="pricing" className="scroll-mt-24">
          <Overline className="mb-6">Planned modules &amp; pricing</Overline>
          <h2 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight max-w-3xl">
            Planned pricing. <span className="text-[#FF8A00]">Published before launch, on principle.</span>
          </h2>
          {c.pricingNote && (
            <p className="text-slate-600 text-lg leading-relaxed mt-4 max-w-3xl">{c.pricingNote}</p>
          )}
          <div className="mt-10">
            <PricingTable
              rows={c.pricingRows}
              footnote={c.pricingFootnote || "Prices in CAD, planned. Final scope and pricing confirmed at launch."}
              testid={`${t}-pricing-table`}
            />
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section testid={`${t}-faq`} className="bg-[#F8F9FA]">
        <Overline className="mb-6">FAQ</Overline>
        <div className="space-y-px bg-slate-200 max-w-4xl">
          {c.faq.map((f, i) => (
            <details key={i} data-testid={`${t}-faq-${i}`} className="bg-white p-6 group">
              <summary className="cursor-pointer font-bold text-lg text-[#0A1224] flex items-center justify-between list-none">
                {f.q}
                <span className="text-[#FF8A00] text-2xl font-black transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </Section>

      <Section testid={`${t}-trust`} className="!py-16"><TrustStrip /></Section>

      {/* FINAL CTA */}
      <Section testid={`${t}-cta`} className="bg-[#0A1224] text-white text-center">
        <h2 className="text-3xl md:text-5xl font-black tracking-tighter max-w-3xl mx-auto leading-tight">
          Be first to know when {c.ctaName || c.name.toLowerCase()} goes live.
        </h2>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <a
            href="#waitlist"
            data-testid={`${t}-final-cta`}
            className="bg-[#FF8A00] text-[#050A15] font-bold px-6 py-3.5 hover:bg-[#FFA833] transition-colors inline-flex items-center gap-2 tracking-tight"
          >
            Join the waitlist
          </a>
          <PrimaryCTA to="/contact" testid={`${t}-walkthrough-cta`}>Book a walkthrough</PrimaryCTA>
        </div>
      </Section>
    </>
  );
}
